/**
 * Registry of supplier-specific quote PDF extraction formats.
 * Backend sets meta.supplierFormat; FE validates allowlist and maps to Materiah supplier by name.
 */

/** Minimum score (0–100) to auto-suggest a Materiah supplier option. */
export const MATERIAH_SUPPLIER_MATCH_THRESHOLD = 55;

/** Map client/parser format ids to registry keys. */
export const QUOTE_FORMAT_ALIASES = {
  rhenium_quote_pdf: 'rhenium',
  enco_scientific_quote_pdf: 'enco_scientific_quote_pdf',
};

/**
 * Strip " — Inner ID: …" suffix from supplier select labels.
 *
 * @param {string} label
 * @returns {string}
 */
export function normalizeSupplierSelectLabel(label) {
  if (!label || typeof label !== 'string') return '';
  return label.split(' — Inner ID:')[0].trim();
}

/**
 * @param {string} text
 * @returns {string}
 */
function normalizeMatchText(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {Object} extractedSupplier
 * @returns {boolean}
 */
function rheniumFingerprintsMatch(extractedSupplier) {
  const name = String(extractedSupplier?.name || '');
  const website = String(extractedSupplier?.website || '').toLowerCase();
  const taxId = String(extractedSupplier?.taxId || '').trim();

  // Rhenium PDFs no longer populate supplier fields; format is verified at parse time.
  if (!name && !website && !taxId) {
    return true;
  }

  const nameHit =
    /rhenium/i.test(name) || /רניום/.test(name);
  const websiteHit = website.includes('rhenium.co.il');
  const taxIdHit = taxId === '511642449';

  return nameHit || websiteHit || taxIdHit;
}

/**
 * Score a Materiah supplier option against extracted supplier fields (name-based).
 *
 * @param {{ value: unknown, label?: string }} option
 * @param {Object} extractedSupplier
 * @returns {{ score: number, reason: string }}
 */
function scoreRheniumMateriahOption(option, extractedSupplier) {
  const labelNorm = normalizeMatchText(normalizeSupplierSelectLabel(option.label));
  const extractedName = normalizeMatchText(extractedSupplier?.name);
  const website = String(extractedSupplier?.website || '').toLowerCase();

  let score = 0;
  const reasons = [];

  if (extractedName && labelNorm) {
    if (labelNorm === extractedName) {
      score += 70;
      reasons.push('exact name');
    } else if (labelNorm.includes(extractedName) || extractedName.includes(labelNorm)) {
      score += 55;
      reasons.push('name overlap');
    }
  }

  if (/rhenium|רניום/.test(labelNorm)) {
    score += 25;
    reasons.push('rhenium in label');
  }

  if (website.includes('rhenium.co.il') && /rhenium|רניום/.test(labelNorm)) {
    score += 30;
    reasons.push('website + label');
  }

  if (
    website.includes('rhenium.co.il') &&
    /rhenium|רניום/.test(labelNorm) &&
    score < MATERIAH_SUPPLIER_MATCH_THRESHOLD
  ) {
    score = MATERIAH_SUPPLIER_MATCH_THRESHOLD;
    reasons.push('rhenium website match');
  }

  const hasExtractedSupplierData =
    Boolean(extractedName) ||
    website.includes('rhenium.co.il') ||
    String(extractedSupplier?.taxId || '').trim() === '511642449';

  if (
    !hasExtractedSupplierData &&
    /rhenium|רניום/.test(labelNorm) &&
    score < MATERIAH_SUPPLIER_MATCH_THRESHOLD
  ) {
    score = MATERIAH_SUPPLIER_MATCH_THRESHOLD;
    reasons.push('rhenium quote format');
  }

  return {
    score: Math.min(100, score),
    reason: reasons.length ? reasons.join(', ') : 'low confidence',
  };
}

/**
 * @param {Object} extractedSupplier
 * @returns {boolean}
 */
function encoFingerprintsMatch(extractedSupplier) {
  const name = String(extractedSupplier?.name || '');
  const hebrewName = String(extractedSupplier?.hebrewName || '');
  const website = String(extractedSupplier?.website || '').toLowerCase();
  const email = String(extractedSupplier?.email || '').toLowerCase();
  const taxId = String(extractedSupplier?.taxId || extractedSupplier?.companyId || '').trim();

  const nameHit =
    /enco\s*scientific/i.test(name) || /scientific\s*services\s*ltd/i.test(name);
  const hebrewHit = /אנקו/.test(hebrewName) || /אנקו/.test(name);
  const websiteHit = website.includes('enco.co.il');
  const emailHit = email.includes('orders@enco.co.il');
  const taxIdHit = taxId === '511233454';

  return nameHit || hebrewHit || websiteHit || emailHit || taxIdHit;
}

/**
 * @param {{ value: unknown, label?: string }} option
 * @param {Object} extractedSupplier
 * @returns {{ score: number, reason: string }}
 */
function scoreEncoMateriahOption(option, extractedSupplier) {
  const labelNorm = normalizeMatchText(normalizeSupplierSelectLabel(option.label));
  const extractedName = normalizeMatchText(extractedSupplier?.name);

  let score = 0;
  const reasons = [];

  if (extractedName && labelNorm) {
    if (labelNorm === extractedName) {
      score += 70;
      reasons.push('exact name');
    } else if (labelNorm.includes(extractedName) || extractedName.includes(labelNorm)) {
      score += 55;
      reasons.push('name overlap');
    }
  }

  if (/enco|אנקו|scientific\s*services/i.test(labelNorm)) {
    score += 30;
    reasons.push('enco in label');
  }

  return {
    score: Math.min(100, score),
    reason: reasons.length ? reasons.join(', ') : 'low confidence',
  };
}

/**
 * @typedef {Object} QuoteSupplierFormatDefinition
 * @property {string} formatId
 * @property {string} displayName
 * @property {boolean} supported
 * @property {(extractedSupplier: Object) => boolean} verifyFingerprints
 * @property {(options: Array, extractedSupplier: Object) => { value: unknown, label: string, score: number, matchReason: string }|null} matchMateriahSupplierOption
 */

/** @type {Record<string, QuoteSupplierFormatDefinition>} */
export const QUOTE_SUPPLIER_FORMATS = {
  rhenium: {
    formatId: 'rhenium',
    displayName: 'Rhenium',
    supported: true,
    verifyFingerprints: rheniumFingerprintsMatch,
    matchMateriahSupplierOption(options, extractedSupplier) {
      const list = Array.isArray(options) ? options : [];
      let best = null;

      for (const option of list) {
        if (option?.value == null || option.value === '') continue;
        const { score, reason } = scoreRheniumMateriahOption(option, extractedSupplier);
        if (score < MATERIAH_SUPPLIER_MATCH_THRESHOLD) continue;
        if (!best || score > best.score) {
          best = {
            value: option.value,
            label: normalizeSupplierSelectLabel(option.label) || String(option.label),
            score,
            matchReason: reason,
          };
        }
      }

      return best;
    },
  },
  enco_scientific_quote_pdf: {
    formatId: 'enco_scientific_quote_pdf',
    displayName: 'ENCO Scientific',
    supported: true,
    verifyFingerprints: encoFingerprintsMatch,
    matchMateriahSupplierOption(options, extractedSupplier) {
      const list = Array.isArray(options) ? options : [];
      let best = null;

      for (const option of list) {
        if (option?.value == null || option.value === '') continue;
        const { score, reason } = scoreEncoMateriahOption(option, extractedSupplier);
        if (score < MATERIAH_SUPPLIER_MATCH_THRESHOLD) continue;
        if (!best || score > best.score) {
          best = {
            value: option.value,
            label: normalizeSupplierSelectLabel(option.label) || String(option.label),
            score,
            matchReason: reason,
          };
        }
      }

      return best;
    },
  },
};

/** @type {string[]} */
export const SUPPORTED_QUOTE_FORMAT_IDS = Object.values(QUOTE_SUPPLIER_FORMATS)
  .filter(f => f.supported)
  .map(f => f.formatId);

/**
 * @param {string} [formatId]
 * @returns {string|null}
 */
export function resolveQuoteFormatRegistryKey(formatId) {
  if (!formatId || typeof formatId !== 'string') return null;
  const key = formatId.trim().toLowerCase();
  return QUOTE_FORMAT_ALIASES[key] || key;
}

export function getQuoteSupplierFormat(formatId) {
  const key = resolveQuoteFormatRegistryKey(formatId);
  if (!key) return null;
  return QUOTE_SUPPLIER_FORMATS[key] || null;
}

/**
 * Resolve format from extracted payload meta.
 *
 * @param {import('../types/quoteExtractionTypes').ExtractedQuoteData} extractedData
 * @returns {{ format: QuoteSupplierFormatDefinition|null, formatId: string|null }}
 */
export function resolveQuoteSupplierFormat(extractedData) {
  const raw =
    extractedData?.meta?.supplierFormat ??
    extractedData?.meta?.parserFormat ??
    extractedData?.meta?.parser_format;
  const rawId =
    raw == null || String(raw).trim() === '' ? null : String(raw).trim().toLowerCase();
  const registryKey = rawId ? resolveQuoteFormatRegistryKey(rawId) : null;
  const format = registryKey ? getQuoteSupplierFormat(registryKey) : null;
  return { format, formatId: registryKey || rawId };
}

/**
 * @param {string} [formatId]
 * @returns {boolean}
 */
export function isSupportedQuoteSupplierFormat(formatId) {
  if (!formatId) return false;
  return SUPPORTED_QUOTE_FORMAT_IDS.includes(String(formatId).trim().toLowerCase());
}
